/**
 * React hook for reading a QMS register.
 *
 * Wraps `registerService.getRecords` with state management
 * (`{ data, isLoading, error, refetch }`) — exactly the harmony-ap
 * `useCompliance` shape. Depends on `useGraphClient` for client + resolved list
 * ids; the list id is looked up from `listIds` by the register's config key
 * (never hard-coded). 04-05 (tiles) and 04-07 (views) consume this.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useGraphClient } from './useGraphClient';
import { getRecords } from '../services/registerService';
import { REGISTERS } from '../config/registers';
import type { RegisterKey, AnyRegisterRecord } from '../types/qms';
import type { ListNameKey } from '../config/sharepoint';

export interface UseRegisterResult {
  data: AnyRegisterRecord[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Read all records for a register. Returns `[]` while loading or when the list
 * is not yet provisioned (the registers are empty today).
 */
export function useRegister(registerKey: RegisterKey): UseRegisterResult {
  const { client, siteId, listIds, isReady } = useGraphClient();
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setFetchTrigger((n) => n + 1);
  }, []);

  // One object per load: a new one whenever any input changes (or on refetch). Loading is
  // derived — "the latest result is not for the latest request" — rather than set inside the
  // effect (13-08, ISS-185: react-hooks/set-state-in-effect). Same states as before.
  const request = useMemo(
    () => (isReady && client && siteId && listIds ? { client, siteId, listIds, registerKey, fetchTrigger } : null),
    [client, siteId, listIds, isReady, registerKey, fetchTrigger],
  );
  const [result, setResult] = useState<{
    request: typeof request;
    data: AnyRegisterRecord[];
    error: string | null;
  }>({ request: null, data: [], error: null });

  useEffect(() => {
    if (!request) return;

    const listId = request.listIds[REGISTERS[request.registerKey].listIdKey as ListNameKey];

    let cancelled = false;

    getRecords(request.client, request.siteId, listId ?? '', request.registerKey)
      .then((items) => {
        if (!cancelled) setResult({ request, data: items, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load register';
          // As before: a failed load keeps the previous data and reports the error.
          setResult((prev) => ({ request, data: prev.data, error: message }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [request]);

  const isLoading = request !== null && result.request !== request;
  return { data: result.data, isLoading, error: isLoading ? null : result.error, refetch };
}
